import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface AuditLog {
  id: string;
  created_at: string;
  action: string;
  performed_by: string;
  entity_type: string;
  entity_id: string | null;
  details: any;
  ip_address: string | null;
}

export default function AuditLogs() {
  const { data: logs = [], isLoading } = useQuery({
    queryKey: ['audit-logs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("audit_logs")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(100);

      if (error) {
        console.error("Error loading audit logs:", error);
        return [];
      }

      return data as AuditLog[];
    },
  });

  const getActionColor = (action: string) => {
    if (action.includes("delete")) return "bg-destructive";
    if (action.includes("create")) return "bg-success";
    if (action.includes("update")) return "bg-warning";
    return "bg-muted";
  };

  const formatDetails = (details: any) => {
    if (!details) return null;

    // For subscriber deletions, show the email
    if (details.email) {
      return (
        <div className="mt-2 text-sm space-y-1">
          <div><span className="text-muted-foreground">Email:</span> {details.email}</div>
          {details.status && <div><span className="text-muted-foreground">Status:</span> {details.status}</div>}
          {details.deleted_at && (
            <div>
              <span className="text-muted-foreground">Deleted:</span>{" "}
              {new Date(details.deleted_at).toLocaleString()}
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold mb-2">Audit Logs</h1>
        <p className="text-sm text-muted-foreground">
          {isLoading ? "Loading..." : `Showing ${logs.length} recent action${logs.length !== 1 ? "s" : ""}`}
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : logs.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            No audit logs found.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {logs.map((log) => (
            <Card key={log.id}>
              <CardHeader className="pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <CardTitle className="text-base font-medium flex items-center gap-2">
                    <Badge className={getActionColor(log.action)}>
                      {log.action.replace(/_/g, " ").toUpperCase()}
                    </Badge>
                    <span className="text-sm text-muted-foreground font-normal">
                      {log.entity_type}
                    </span>
                  </CardTitle>
                  <span className="text-xs text-muted-foreground">
                    {new Date(log.created_at).toLocaleString()}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-muted-foreground">Performed by:</span>{" "}
                    <span className="font-medium">{log.performed_by}</span>
                  </div>
                  {log.ip_address && (
                    <div>
                      <span className="text-muted-foreground">IP Address:</span>{" "}
                      <span className="font-mono text-xs">{log.ip_address}</span>
                    </div>
                  )}
                  {log.entity_id && (
                    <div className="sm:col-span-2">
                      <span className="text-muted-foreground">Entity ID:</span>{" "}
                      <span className="font-mono text-xs">{log.entity_id}</span>
                    </div>
                  )}
                </div>
                {formatDetails(log.details)}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
